<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { GroupsService } from '@client/services/groups';
  import { RoutingService } from '@client/services/routing';
  import { SignaturesService } from '@client/services/signatures';

  import { appStore } from '@client/stores/app';

  import { RoutingList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let selectedRouteId = null;
  let routes = [];

  const selectRoute = (routeId) => {
    selectedRouteId = routeId;
  };

  const executeDelete = async () => {
    $appStore.loading = true;

    try {
      const route = routes.find((r) => r._id === selectedRouteId);

      if (!route) {
        throw new Error($format('label.FAILURE.ROUTE_NOT_FOUND'));
      }

      await RoutingService.deleteRoute(route);

      notify($format('label.SUCCESS.DELETE_ROUTE'));

      step.data = {
        id: route.blockname,
        priority: route.priority,
        rollbackData: {
          route: route
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.DELETE_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackDelete = async () => {
    $appStore.loading = true;

    try {
      const { route } = step.data.rollbackData;

      if (!route) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      // Recreate the route with the same data
      const payload = {
        blockname: route.blockname,
        hide: route.hide,
        priority: route.priority - 1,
        header: route.header,
        sendergroups: route.sendergroups || [],
        status: route.status,
        tagblock: route.tagblock || {},
        tags: route.tags || []
      };

      await RoutingService.updateRoute(payload);

      notify($format('label.SUCCESS.ROLLBACK_ROUTE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const [signaturesResponse, groupsResponse] = await Promise.all([
        SignaturesService.getAll(),
        GroupsService.getAll()
      ]);

      const signatures = Array.isArray(signaturesResponse) ? signaturesResponse : [];
      const groups = Array.isArray(groupsResponse) ? groupsResponse : [];

      routes = signatures.map((signature, index) => {
        const sendergroups = groups.filter(
          ({ groupname }) => signature.sendergroups.filter((group) => groupname === group.groupname).length
        );

        const selectedTags = (signature.tags || []).map((tag) => ({
          label: tag.text,
          value: tag.text
        }));

        return {
          ...signature,
          priority: index + 1,
          sendergroups,
          selectedTags
        };
      });
    } catch (error) {
      console.error('Failed to load signature routing data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeDelete);
    }

    if (onRollback) {
      onRollback(rollbackDelete);
    }
  });

  $: nextDisabled = !selectedRouteId || loading;
</script>

<div class="signature-routing-delete-form">
  <RoutingList {routes} bind:selectedRouteId onSelectRoute={selectRoute} showTags={true} {loading} />
</div>

<style lang="scss">
  .signature-routing-delete-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }
  }
</style>
